import React, { useCallback, useMemo } from "react";

import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { Drawer } from "components/Measure-data/Drawer/Drawer";
import { GroupsForm } from "components/Measure-data/Groups-form/GroupsForm";
import { Group, MeasureDataGroup, MeasureDataRound } from "components/Measure-data/Groups-form/GroupsFormContext";
import { DEBT_MEASURE_ROLES } from "constants/debt";
import { useActionHook } from "hooks";
import { useDealActions } from "store/actions-context";
import { useDealDefaultCurrencySelect, useDealSubAssetsSelector } from "store/selectors/deal-selectors/deal-selectors";
import { useCreateManyMeasureDataSelect } from "store/selectors/view-selectors";
import { CustomPayment, MeasureDataDraft } from "store/types";

import { type EnhancedMeasureData, useMeasureDrawer } from "../../hooks/debt-hooks/use-measure-drawer";
import type { PrepaymentFormData } from "./PrepaymentDrawer";

interface PrepaymentDrawerValuesProps {
  open: boolean;
  rounds: Record<number, MeasureDataRound[]>;
  onClose: () => void;
  dealId: number;
  createPrepayments: () => Promise<CustomPayment>;
  prepaymentFormData: PrepaymentFormData;
}

export const PrepaymentDrawerValues: React.FC<PrepaymentDrawerValuesProps> = ({
  open,
  rounds,
  onClose,
  dealId: sourceDealId,
  createPrepayments,
  prepaymentFormData
}) => {
  const { addMeasureToForm, debtMeasures } = useMeasureDrawer([sourceDealId]);
  const { createManyMeasureDataAction, saveMeasureDataDraftAction } = useDealActions();
  const createMeasureData = useCreateManyMeasureDataSelect();
  const { action: createManyMeasureData, loading } = useActionHook(createManyMeasureDataAction);
  const currency = useDealDefaultCurrencySelect() || "USD";

  // Find measures for different payment types
  const isRefinance = prepaymentFormData.prepaymentType === "refinance";
  const principalMeasure = debtMeasures.find(
    (param) =>
      param.roles[0] === (isRefinance ? DEBT_MEASURE_ROLES.principalRefinance : DEBT_MEASURE_ROLES.principalPrepayment)
  );
  const interestPaymentMeasure = debtMeasures.find((param) => param.roles[0] === DEBT_MEASURE_ROLES.interest);
  const pikInterestPaymentMeasure = debtMeasures.find((param) => param.roles[0] === DEBT_MEASURE_ROLES.pikInterest);
  const vatPaymentMeasure = debtMeasures.find((param) => param.roles[0] === DEBT_MEASURE_ROLES.vat);
  const callProtectionFeeMeasure = debtMeasures.find(
    (param) => param.roles[0] === DEBT_MEASURE_ROLES.callProtectionFee
  );
  const yieldMaintenanceFeeMeasure = debtMeasures.find(
    (param) => param.roles[0] === DEBT_MEASURE_ROLES.yieldMaintenanceFee
  );
  const fixedAmountFeeMeasure = debtMeasures.find((param) => param.roles[0] === DEBT_MEASURE_ROLES.fixedFee);
  const creditFacilityFeeMeasure = debtMeasures.find(
    (param) => param.roles[0] === DEBT_MEASURE_ROLES.creditFacilityFee
  );

  const subDeals = useDealSubAssetsSelector();

  const updateCustomPaymentId = useCallback(
    (
      groups: MeasureDataGroup[],
      prepayment: CustomPayment,
      type: 
        "principal_measure_data_id" | 
        "interest_measure_data_id" |
        "pik_interest_measure_data_id" |
        "vat_measure_data_id" | 
        "fee_measure_data_id"
    ) => {
      const groupsOfType = groups.filter((g) => g.data[0].custom_payment_type === type);
      if (groupsOfType.length > 0) {
        if (type === "fee_measure_data_id") {
          groupsOfType.forEach((group) => {
            group.data[0].custom_payment_id = prepayment.fees.find(
              (f) => f.type === (group.data[0] as EnhancedMeasureData).fee_type
            )?.id;
          });
        } else {
          groupsOfType.forEach((group) => {
            group.data[0].custom_payment_id = prepayment.id;
          });
        }
      }
    },
    []
  );

  const postMeasureDataAndPrepayments = useCallback(
    async (data: { groups: MeasureDataGroup[]; roundsByDeals: Record<number, MeasureDataRound[]> }) => {
      // First create the custom payments (prepayments)
      const prepayment = await createPrepayments();

      updateCustomPaymentId(data.groups, prepayment, "principal_measure_data_id");
      updateCustomPaymentId(data.groups, prepayment, "interest_measure_data_id");
      updateCustomPaymentId(data.groups, prepayment, "pik_interest_measure_data_id");
      updateCustomPaymentId(data.groups, prepayment, "vat_measure_data_id");
      updateCustomPaymentId(data.groups, prepayment, "fee_measure_data_id");

      // Then create the measure data
      await createManyMeasureData(data);

      onClose();
    },
    [createManyMeasureData, createPrepayments, sourceDealId, onClose]
  );

  const handleSaveAsDraft = useCallback(
    async (data: MeasureDataDraft["data"]) => {
      if (!createMeasureData?.dealId) return;

      await saveMeasureDataDraftAction({
        deal_id: createMeasureData.dealId,
        data,
        ...(createMeasureData.draftId && { id: createMeasureData.draftId })
      });
      onClose();
    },
    [createManyMeasureData, saveMeasureDataDraftAction, createMeasureData?.dealId, createMeasureData?.draftId, onClose]
  );

  const handleSkip = useCallback(async () => {
    await createPrepayments();
    onClose();
  }, [createPrepayments, onClose]);

  const initialData = useMemo(() => {
    const measureData: EnhancedMeasureData[] = [];
    const groups: Group[] = [];

    if (rounds) {
      const roundsForDeal = rounds[sourceDealId];
      if (roundsForDeal?.length) {
        const firstRound = roundsForDeal[0];
        const addMeasure = addMeasureToForm(firstRound, measureData, groups);

        // Create measure data for principal payment
        if (prepaymentFormData.principalAmount && principalMeasure) {
          addMeasure({
            id: prepaymentFormData.principalId ?? undefined,
            value: prepaymentFormData.principalAmount,
            date: prepaymentFormData.effectiveDate,
            subDealId: sourceDealId,
            measureId: principalMeasure.id,
            customPaymentType: "principal_measure_data_id"
          });
        }

        // Create measure data for interest payment
        if (prepaymentFormData.includeInterest && prepaymentFormData.interestAmount && interestPaymentMeasure) {
          addMeasure({
            id: prepaymentFormData.interestId ?? undefined,
            value: prepaymentFormData.interestAmount,
            date: prepaymentFormData.effectiveDate,
            subDealId: sourceDealId,
            measureId: interestPaymentMeasure.id,
            customPaymentType: "interest_measure_data_id"
          });
        }

        // Create measure data for interest payment
        if (prepaymentFormData.includePikInterest && prepaymentFormData.pikInterestAmount && pikInterestPaymentMeasure) {
          addMeasure({
            id: prepaymentFormData.pikInterestId ?? undefined,
            value: prepaymentFormData.pikInterestAmount,
            date: prepaymentFormData.effectiveDate,
            subDealId: sourceDealId,
            measureId: pikInterestPaymentMeasure.id,
            customPaymentType: "pik_interest_measure_data_id"
          });
        }

        // Create measure data for VAT payment
        if (prepaymentFormData.includeVAT && prepaymentFormData.vatAmount && vatPaymentMeasure) {
          addMeasure({
            id: prepaymentFormData.vatId ?? undefined,
            value: prepaymentFormData.vatAmount,
            date: prepaymentFormData.effectiveDate,
            subDealId: sourceDealId,
            measureId: vatPaymentMeasure.id,
            customPaymentType: "vat_measure_data_id"
          });
        }

        // Create measure data for credit facility fee
        if (prepaymentFormData.creditFacilityFeeAmount && creditFacilityFeeMeasure) {
          addMeasure({
            id: prepaymentFormData.creditFacilityFeeId ?? undefined,
            value: prepaymentFormData.creditFacilityFeeAmount,
            date: prepaymentFormData.effectiveDate,
            subDealId: sourceDealId,
            measureId: creditFacilityFeeMeasure.id,
            customPaymentType: "fee_measure_data_id"
          });
        }

        // Create measure data for fees
        prepaymentFormData.fees.forEach((fee) => {
          if (fee.amount) {
            let feeMeasure;
            if (fee.type === "call_protection") {
              feeMeasure = callProtectionFeeMeasure;
            } else if (fee.type === "yield_maintenance") {
              feeMeasure = yieldMaintenanceFeeMeasure;
            } else if (fee.type === "fixed_amount") {
              feeMeasure = fixedAmountFeeMeasure;
            }

            if (feeMeasure) {
              addMeasure({
                id: fee.measure_data_id ?? undefined,
                value: fee.amount,
                date: prepaymentFormData.effectiveDate,
                subDealId: sourceDealId,
                measureId: feeMeasure.id,
                customPaymentType: "fee_measure_data_id",
                feeType: fee.type
              });
            }
          }
        });
      }
    }

    return { measureData, groups };
  }, [
    rounds,
    prepaymentFormData,
    sourceDealId,
    principalMeasure,
    interestPaymentMeasure,
    vatPaymentMeasure,
    callProtectionFeeMeasure,
    yieldMaintenanceFeeMeasure,
    fixedAmountFeeMeasure,
    creditFacilityFeeMeasure
  ]);

  return (
    <Drawer
      anchor="left"
      open={open}
      handleClose={onClose}
      title={isRefinance ? "Add Refinancing Payments" : "Add Prepayment Values"}
    >
      <AsyncContentMui isLoading={loading}>
        {rounds && (
          <GroupsForm
            deal_id={sourceDealId}
            subDeals={subDeals}
            parentId={null}
            initialRoundsByDeal={rounds}
            initialGroups={initialData.groups}
            initialMeasureData={initialData.measureData}
            onSave={postMeasureDataAndPrepayments}
            onSaveAsDraft={handleSaveAsDraft}
            onClose={onClose}
            onSkip={handleSkip}
            disableDealSelect
            disableSubDealSelect
            defaultCurrency={currency}
          />
        )}
      </AsyncContentMui>
    </Drawer>
  );
};
